import { createAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { ScheduledDocumentPreview } from '@app/features/documents/types';
import {
  TEMPLATE__FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
  TEMPLATE__NEW_TEMPLATE_SAVE_FAILURE,
  TEMPLATE__NEW_TEMPLATE_SAVE_REQUEST,
  TEMPLATE__NEW_TEMPLATE_SAVE_SUCCESS,
  TEMPLATE_ATTACHMENT_CLEAR,
  TEMPLATE_ATTACHMENT_SAVE,
  TEMPLATE_CLEAR_ALL_STATE,
  TEMPLATE_CREATE__PDF_LOAD_SUCCESS,
  TEMPLATE_CREATE_FAILURE,
  TEMPLATE_EDIT__PDF_LOAD_FAILURE,
  TEMPLATE_EDIT__PDF_LOAD_REQUEST,
  TEMPLATE_EDIT__PDF_LOAD_SUCCESS,
  TEMPLATE_EDIT_FAILURE,
  TEMPLATE_EDIT_REQUEST,
  TEMPLATE_EDIT_SUCCESS,
  TEMPLATE_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
  TEMPLATE_FIELD__CREATION_CANCEL,
  TEMPLATE_FIELD_CHECKBOX_GROUP_UPDATE_INDICES,
  TEMPLATE_FIELD_CONTEXT_MENU_CHANGE,
  TEMPLATE_FIELD_CREATE,
  TEMPLATE_FIELD_DATA_SET,
  TEMPLATE_FIELD_DELETE,
  TEMPLATE_FIELD_POSITION_CHANGE,
  TEMPLATE_FIELD_SIZE_CHANGE,
  TEMPLATE_FIELD_TARGET_SET,
  TEMPLATE_FILE_CHANGE__PDF_INITIALIZE,
  TEMPLATE_FILE_CHANGE__PDF_LOAD_SUCCESS,
  TEMPLATE_INFORMATION_FETCH_FAILURE,
  TEMPLATE_INFORMATION_FETCH_REQUEST,
  TEMPLATE_INFORMATION_FETCH_SUCCESS,
  TEMPLATE_LABEL_IDS_CHANGE,
  TEMPLATE_LOADING_FINISH,
  TEMPLATE_LOADING_START,
  TEMPLATE_PARTICIPANT_DELETE,
  TEMPLATE_TITLE_CHANGE,
} from '@app/store/actionTypes';
import AllowSignatureTypes from '@app/store/AllowSignatureTypes';
import Draft from '@app/store/embedded/Draft';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import { HostReference } from '@app/store/Host';
import MeansType from '@app/store/MeansType';
import SigningProgressType from '@app/store/SigningProgressType';
import Request from '@app/store/template/AttachmentRequest';
import FieldInformation from '@app/store/template/FieldInformation';
import FieldMetaData from '@app/store/template/FieldMetaData';
import PropType from '@app/store/template/PropType';
import Template, { TemplateData } from '@app/store/template/Template';
import TextAlign from '@app/store/TextAlign';
import { Thunk } from '@app/store/types';

import * as selectors from './selectors';

export function templateAttachmentClear() {
  return {
    type: TEMPLATE_ATTACHMENT_CLEAR,
  };
}

export function templateAttachmentSave(payload: { participantId: string; requests: Request[] }) {
  return {
    type: TEMPLATE_ATTACHMENT_SAVE,
    payload,
  };
}

export function templateClearAllState() {
  return {
    type: TEMPLATE_CLEAR_ALL_STATE,
  };
}

export function templateLoading() {
  return {
    type: TEMPLATE_LOADING_START,
  };
}

export const templateLoadingFinish = createAction(TEMPLATE_LOADING_FINISH);

export function templateCreateFailure() {
  return {
    type: TEMPLATE_CREATE_FAILURE,
  };
}

export const templateFieldCheckboxGroupIndicesUpdate = createAction(
  TEMPLATE_FIELD_CHECKBOX_GROUP_UPDATE_INDICES,
  (groupLabel, indicesById: { id: string; index: number }[]) => {
    return {
      payload: {
        groupLabel,
        indicesById,
      },
    };
  }
);

export function templateFieldContextMenuChange(
  id: string,
  propType: PropType,
  value: FontType | TextAlign | boolean | number | string
) {
  return {
    type: TEMPLATE_FIELD_CONTEXT_MENU_CHANGE,
    payload: {
      id,
      propType,
      value,
    },
  };
}

export const setupTemplateByDraft = createAction<Draft, 'template/setupTemplateByDraft'>(
  'template/setupTemplateByDraft'
);

export const changeTemplateSigningProgressType = createAction<
  SigningProgressType,
  'template/changeSigningProgressType'
>('template/changeSigningProgressType');

export function templateFieldCreate(payload: {
  meta: FieldMetaData;
  information: FieldInformation;
}) {
  return {
    type: TEMPLATE_FIELD_CREATE,
    payload,
  };
}

export function templateFieldDelete(id: string, participantId: string, page: number) {
  return {
    type: TEMPLATE_FIELD_DELETE,
    payload: {
      id,
      participantId,
      page,
    },
  };
}

export const templateDeleteFieldsOverMaxDocumentPage = createAction(
  TEMPLATE__FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
  (payload: { fieldIds: string[]; pages: string[] }) => {
    return {
      payload,
    };
  }
);

export function templateFieldPositionChange(id: string, relativeX: number, relativeY: number) {
  return {
    type: TEMPLATE_FIELD_POSITION_CHANGE,
    payload: {
      id,
      relativeX,
      relativeY,
    },
  };
}

export function templateFieldSizeChange(
  id: string,
  relativeHeight: number,
  relativeWidth: number,
  currentScaleHeight: number,
  currentScaleWidth: number
) {
  return {
    type: TEMPLATE_FIELD_SIZE_CHANGE,
    payload: {
      currentScaleHeight,
      currentScaleWidth,
      id,
      relativeHeight,
      relativeWidth,
    },
  };
}

export function templateFieldTargetSet(
  payload:
    | {
        participantId: string;
        type: FieldType.CHECKBOX_GROUP | FieldType.TEXT_FIELD;
      }
    | {
        allowSignatureTypes: AllowSignatureTypes;
        participantId: string;
        type: FieldType.SIGNATURE_FIELD;
      }
) {
  return {
    type: TEMPLATE_FIELD_TARGET_SET,
    payload: {
      ...payload,
    },
  };
}

export function templateEditRequest(payload: { fileId: string | null }) {
  return {
    type: TEMPLATE_EDIT_REQUEST,
    payload,
  };
}

export function templateEditSuccess() {
  return {
    type: TEMPLATE_EDIT_SUCCESS,
  };
}

export function templateEditFailure() {
  return {
    type: TEMPLATE_EDIT_FAILURE,
  };
}
export function templateSaveNewTemplate(payload: { title: string }) {
  return {
    type: TEMPLATE__NEW_TEMPLATE_SAVE_REQUEST,
    payload,
  };
}

export const templateSaveNewTemplateSuccess = createAction(TEMPLATE__NEW_TEMPLATE_SAVE_SUCCESS);

export function templateSaveNewTemplateFailure() {
  return {
    type: TEMPLATE__NEW_TEMPLATE_SAVE_FAILURE,
    error: true,
  };
}
export function templateInformationFetchRequest(payload: string) {
  return {
    type: TEMPLATE_INFORMATION_FETCH_REQUEST,
    payload,
  };
}

export function templateInformationFetchSuccess(payload: Template) {
  return {
    type: TEMPLATE_INFORMATION_FETCH_SUCCESS,
    payload,
  };
}

export const templateDataFetched = createAction<TemplateData, 'templates/dataFetched'>(
  'templates/dataFetched'
);

export function templateInformationFetchFailure() {
  return {
    type: TEMPLATE_INFORMATION_FETCH_FAILURE,
  };
}

export function templateCreateLoadPdfSuccess(payload: {
  fileId: string;
  pdfArrayBuffer: ArrayBuffer;
  title: string;
  url: string;
}) {
  return {
    type: TEMPLATE_CREATE__PDF_LOAD_SUCCESS,
    payload,
  };
}

export function templateEditLoadPdfRequest(payload: { url: string }) {
  return {
    type: TEMPLATE_EDIT__PDF_LOAD_REQUEST,
    payload,
  };
}

export function templateEditLoadPdfSuccess(payload: { pdfArrayBuffer: ArrayBuffer }) {
  return {
    type: TEMPLATE_EDIT__PDF_LOAD_SUCCESS,
    payload,
  };
}

export function templateEditLoadPdfFailure(payload: { error: AxiosError }) {
  return {
    type: TEMPLATE_EDIT__PDF_LOAD_FAILURE,
    payload,
  };
}

export function templateFileChangePdfInitialize() {
  return {
    type: TEMPLATE_FILE_CHANGE__PDF_INITIALIZE,
  };
}

export function templateFileChangeLoadPdfSuccess(payload: {
  fileId: string;
  pdfArrayBuffer: ArrayBuffer;
  title: string;
  url: string;
}) {
  return {
    type: TEMPLATE_FILE_CHANGE__PDF_LOAD_SUCCESS,
    payload,
  };
}

export const setTemplateParticipants = createAction<
  Array<{
    id: string;
    means?: string;
    meansType?: MeansType;
    name?: string;
    role: string;
    host?: HostReference;
  }>,
  'template/setTemplateParticipants'
>('template/setTemplateParticipants');

export const templateParticipantDelete = createAction(
  TEMPLATE_PARTICIPANT_DELETE,
  (id: string, fieldIds: string[]) => {
    return {
      payload: {
        id,
        fieldIds,
      },
    };
  }
);

export function templateTitleChange(value: string) {
  return {
    type: TEMPLATE_TITLE_CHANGE,
    payload: { value },
  };
}

export function templateFieldDataSet(payload: {
  page: number;
  relativeX: number;
  relativeY: number;
}) {
  return {
    type: TEMPLATE_FIELD_DATA_SET,
    payload,
  };
}

export function templateSelectAllowSignatureTypes(payload: {
  allowSignatureTypes: AllowSignatureTypes;
  participantId: string;
}) {
  return {
    type: TEMPLATE_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
    payload,
  };
}

export function templateFieldCancelCreation() {
  return {
    type: TEMPLATE_FIELD__CREATION_CANCEL,
  };
}

export const scheduledDocumentPreviewDataFetched = createAction<
  ScheduledDocumentPreview,
  'template/scheduledDocumentPreviewDataFetched'
>('template/scheduledDocumentPreviewDataFetched');

export function templateLabelIdsChange(payload: { labelIds: string[] }) {
  return {
    type: TEMPLATE_LABEL_IDS_CHANGE,
    payload,
  };
}

export type TemplateAction =
  | ReturnType<typeof scheduledDocumentPreviewDataFetched>
  | ReturnType<typeof changeTemplateSigningProgressType>
  | ReturnType<typeof setTemplateParticipants>
  | ReturnType<typeof setupTemplateByDraft>
  | ReturnType<typeof templateAttachmentClear>
  | ReturnType<typeof templateAttachmentSave>
  | ReturnType<typeof templateClearAllState>
  | ReturnType<typeof templateCreateFailure>
  | ReturnType<typeof templateLoading>
  | ReturnType<typeof templateLoadingFinish>
  | ReturnType<typeof templateCreateLoadPdfSuccess>
  | ReturnType<typeof templateDeleteFieldsOverMaxDocumentPage>
  | ReturnType<typeof templateEditFailure>
  | ReturnType<typeof templateEditRequest>
  | ReturnType<typeof templateEditSuccess>
  | ReturnType<typeof templateSaveNewTemplate>
  | ReturnType<typeof templateSaveNewTemplateSuccess>
  | ReturnType<typeof templateSaveNewTemplateFailure>
  | ReturnType<typeof templateEditLoadPdfFailure>
  | ReturnType<typeof templateEditLoadPdfRequest>
  | ReturnType<typeof templateEditLoadPdfSuccess>
  | ReturnType<typeof templateFieldCancelCreation>
  | ReturnType<typeof templateFieldCheckboxGroupIndicesUpdate>
  | ReturnType<typeof templateFieldContextMenuChange>
  | ReturnType<typeof templateFieldCreate>
  | ReturnType<typeof templateFieldDataSet>
  | ReturnType<typeof templateFieldDelete>
  | ReturnType<typeof templateFieldPositionChange>
  | ReturnType<typeof templateFieldSizeChange>
  | ReturnType<typeof templateFieldTargetSet>
  | ReturnType<typeof templateFileChangePdfInitialize>
  | ReturnType<typeof templateFileChangeLoadPdfSuccess>
  | ReturnType<typeof templateInformationFetchFailure>
  | ReturnType<typeof templateInformationFetchRequest>
  | ReturnType<typeof templateInformationFetchSuccess>
  | ReturnType<typeof templateParticipantDelete>
  | ReturnType<typeof templateSelectAllowSignatureTypes>
  | ReturnType<typeof templateTitleChange>
  | ReturnType<typeof templateLabelIdsChange>
  | ReturnType<typeof templateDataFetched>;

export const deleteParticipant =
  (id: string): Thunk =>
  (dispatch, getState) => {
    const participantField = selectors.participantFieldSelector(getState());
    const fieldIds = participantField[id];

    dispatch(templateParticipantDelete(id, fieldIds));
  };

/**
 * PDF 파일을 교체하여 페이지 수가 변경되고, 현재 PDF 페이지 범위 밖에 필드 정보가 있는경우
 * 해당 필드 정보를 삭제합니다.
 */
export const deletePageOverParticipantFields =
  ({ maxPage }: { maxPage: number }): Thunk =>
  (dispatch, getState) => {
    const fieldIdsInPages = selectors.idsInPagesSelector(getState());
    const fieldPages = Object.keys(fieldIdsInPages).map((page) => parseInt(page, 10) ?? 0);

    // 서명자 필드 지우기
    const deleteFieldIds = Object.entries(fieldIdsInPages)
      .filter(([page, _ids]) => (parseInt(page, 10) ?? 0) > maxPage)
      .map(([_page, ids]) => ids)
      .flat();
    const deletePages = fieldPages.filter((page) => page > maxPage).map((page) => page.toString());
    dispatch(
      templateDeleteFieldsOverMaxDocumentPage({ fieldIds: deleteFieldIds, pages: deletePages })
    );
  };
